import React, { useEffect } from 'react';
import { Box, Grid } from '@mui/material';
import { useFormValidator } from '../../../../common/inputs/useFormValidator';
import { CountryDropdown, ValidatedTextInput } from '../../../../common/inputs/ValidatedInputs';
import PhoneNumberInput from '../../../../common/inputs/PhoneNumberInput';
import { withQueryData } from '../../../../common/withQueryData';
import { useConfigurationData } from '../../../../common/hooks/useConfigurationData';

const style = { margin: '4px 0' };

/**
 * Customer details form with validated inputs. Uses useFormValidator and ValidatedInputs.
 * Props: customerInfo (object), onCustomerChange(field, value), onValidChange(valid), loading (disabled).
 */
const CustomerDetailsForm = (props) => {
  const { customerInfo = {}, onCustomerChange, onValidChange, loading } = props;

  const [valid, setValid, , , formChanged, onFormChange] = useFormValidator();

  const { countriesQueryResults } = useConfigurationData();

  const onChange = (id, value, valid) => {
    onCustomerChange(id, value);
    onFormChange();
    setValid(id, valid);
  };

  useEffect(() => {
    onValidChange?.(valid);
  }, [onValidChange, valid]);

  return (
    <CustomerDetailsFormContentWithQueryData
      queryData={countriesQueryResults}
      customerInfo={customerInfo}
      onChange={onChange}
      disableError={!formChanged}
      loading={loading}
    />
  );
};

const CustomerDetailsFormContent = ({
  data: countries = [],
  customerInfo,
  onChange,
  disableError,
  loading,
}) => {
  const selectedCountry =
    customerInfo.countryCode && countries.find((c) => c.code === customerInfo.countryCode);
  const selectedCountryPrefix =
    customerInfo.countryPrefix && countries.find((c) => c.phonePrefix === customerInfo.countryPrefix);

  const onChangeCountry = (id, country, valid) => {
    onChange(id, country?.code ?? '', valid);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <ValidatedTextInput
            fullWidth
            size="small"
            id="firstName"
            onChange={onChange}
            label="Imię"
            required
            sx={style}
            value={customerInfo.firstName ?? ''}
            disableError={disableError}
            disabled={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ValidatedTextInput
            fullWidth
            size="small"
            id="lastName"
            onChange={onChange}
            label="Nazwisko"
            required
            sx={style}
            value={customerInfo.lastName ?? ''}
            disableError={disableError}
            disabled={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ValidatedTextInput
            fullWidth
            size="small"
            id="email"
            onChange={onChange}
            label="E-mail"
            required
            sx={style}
            value={customerInfo.email ?? ''}
            disableError={disableError}
            disabled={loading}
            email
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <PhoneNumberInput
            prefixValue={selectedCountryPrefix}
            numberValue={customerInfo.phoneNumber ?? ''}
            countries={countries}
            onChange={onChange}
            disableError={disableError}
            loading={loading}
            sx={{ width: '100%' }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ValidatedTextInput
            fullWidth
            size="small"
            id="address"
            label="Adres"
            type="text"
            multiline
            rows={2}
            onChange={onChange}
            disabled={loading}
            value={customerInfo.address ?? ''}
            sx={style}
            disableError={disableError}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CountryDropdown
            id="countryCode"
            value={selectedCountry}
            onChange={onChangeCountry}
            countries={countries}
            sx={style}
            disableError={disableError}
            disabled={loading}
            textFieldProps={{
              label: 'Kraj',
              size: 'small',
            }}
            getOptionLabel={(country) => country.name}
            getOptionText={(country) => country.name}
          />
        </Grid>
        <Grid item xs={12}>
          <ValidatedTextInput
            fullWidth
            size="small"
            id="comments"
            label="Uwagi (opcjonalnie)"
            type="text"
            multiline
            rows={3}
            onChange={onChange}
            disabled={loading}
            value={customerInfo.comments ?? ''}
            sx={style}
            disableError={disableError}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const CustomerDetailsFormContentWithQueryData = withQueryData(CustomerDetailsFormContent);

export default CustomerDetailsForm;
